'use client';

import { useState, forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, ChevronRight, Phone } from 'lucide-react';
import Container from '@/components/ui/container';
import TopBar from './TopBar';
import AnnouncementBanner from './AnnouncementBanner';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useNavigation } from '@/context/NavigationContext';
import { trackEvent } from '@/lib/analytics';

interface NavigationBarProps {
	navigation: any;
	globals: any;
}

const NavigationBar = forwardRef<HTMLElement, NavigationBarProps>(({ globals, navigation }, ref) => {
	const directusURL = process.env.NEXT_PUBLIC_DIRECTUS_URL;
	const lightLogoUrl = globals?.logo
		? `${directusURL}/assets/${globals.logo}`
		: '/images/breuerklimaattechniek-logo.webp';
	// Dark logo removed as dark mode is no longer a toggle feature for users, but we keep the variable if needed explicitly
	// const darkLogoUrl = globals?.logo_dark_mode ? `${directusURL}/assets/${globals.logo_dark_mode}` : '';

	const pathname = usePathname();

	const [isVisible, setIsVisible] = useState(true);
	const lastScrollY = useRef(0);
	const bannerRef = useRef<HTMLDivElement>(null);
	const [bannerHeight, setBannerHeight] = useState(0);

	useLayoutEffect(() => {
		if (!bannerRef.current) return;
		const observer = new ResizeObserver(entries => setBannerHeight(entries[0].contentRect.height));
		observer.observe(bannerRef.current);
		setBannerHeight(bannerRef.current.offsetHeight);
		return () => observer.disconnect();
	}, [globals?.announcement_enabled, globals?.announcement_text]);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			// Show navbar if at the very top
			if (currentScrollY < 10) {
				setIsVisible(true);
				lastScrollY.current = currentScrollY;

				return;
			}

			// Determine scroll direction
			// If scrolling down (current > last) -> Hide
			// If scrolling up (current < last) -> Show
			if (currentScrollY > lastScrollY.current + 5) {
				// Scrolling down
				setIsVisible(false);
			} else if (currentScrollY < lastScrollY.current - 5) {
				// Scrolling up
				setIsVisible(true);
			}

			lastScrollY.current = currentScrollY;
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	// Logic to resolve href based on type
	const getHref = (item: any) => {
		if (item.type === 'page' && item.page?.permalink) {
			const permalink = item.page.permalink;

			return permalink.startsWith('/') ? permalink : `/${permalink}`;
		}
		if (item.type === 'url' && item.url) {
			return item.url;
		}

		return '#';
	};

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	// Close mobile menu on path change
	useEffect(() => {
		setIsMobileMenuOpen(false);
	}, [pathname]);

	const { displayTopbar, displayMainnav } = useNavigation();
	const navItems = navigation?.items || [];
	const ctaItem = navItems.find(
		(item: any) =>
			item.url?.includes('offerte') ||
			(item.type === 'page' && item.page?.permalink?.includes('offerte')) ||
			item.title?.toLowerCase().includes('offerte'),
	);
	const mainItems = navItems.filter((item: any) => item !== ctaItem);

	return (
		<>
			{globals?.announcement_enabled && globals?.announcement_text && (
				<div ref={bannerRef} className="sticky top-0 z-[51] w-full">
					<AnnouncementBanner text={globals.announcement_text} />
				</div>
			)}
			<header
				ref={ref}
				style={{ top: bannerHeight }}
				className={cn(
					'sticky z-50 w-full flex flex-col transition-transform duration-300 ease-in-out',
					!isVisible && '-translate-y-full',
				)}
			>
			{displayTopbar && <TopBar />}
			{displayMainnav && (
				<div className="bg-background border-b border-border shadow-sm">
					<Container className="flex items-center justify-between py-4">
						<Link href="/" className="flex-shrink-0 relative z-10">
							<Image
								src={lightLogoUrl}
								alt="Breuer Klimaattechniek"
								width={180}
								height={80}
								className="h-auto object-contain w-[140px] md:w-[160px]"
								priority
							/>
						</Link>

						{/* Desktop Navigation */}
						{displayMainnav && (
							<div className="hidden lg:flex items-center gap-8">
								<nav className="flex items-center gap-8">
									{mainItems.map((item: any) => (
										<Link
											key={item.id}
											href={getHref(item)}
											className={cn(
												'text-blue-dark font-heading font-bold text-lg hover:text-orange transition-colors',
												pathname === getHref(item) && 'text-orange',
											)}
											onClick={() => trackEvent('navigation_click', { item: item.title, location: 'desktop' })}
										>
											{item.title}
										</Link>
									))}
									<Button
										asChild
										className="bg-orange hover:bg-orange-hover text-white font-bold px-6 ml-4 shadow-md transition-transform hover:scale-105 active:scale-95"
									>
										<Link href={ctaItem ? getHref(ctaItem) : '/contact'} onClick={() => trackEvent('navigation_click', { item: 'cta_offerte', location: 'desktop' })}>
											{ctaItem ? ctaItem.title : 'Offerte aanvragen'}
										</Link>
									</Button>
								</nav>
							</div>
						)}

						{displayMainnav && (
							<div className="lg:hidden">
								<Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
									<SheetTrigger asChild>
										<Button variant="ghost" size="icon" className="text-blue-dark">
											<Menu className="size-8" />
											<span className="sr-only">Open menu</span>
										</Button>
									</SheetTrigger>
									<SheetContent side="right" className="w-[300px] sm:w-[400px] flex flex-col bg-white">
										<nav className="flex flex-col gap-6 mt-8 flex-grow">
											<div className="flex flex-col gap-4 border-b pb-4">
												{mainItems.map((item: any) => (
													<Link
														key={item.id}
														href={getHref(item)}
														className="text-blue-dark font-heading font-bold text-xl flex items-center justify-between group"
														onClick={() => trackEvent('navigation_click', { item: item.title, location: 'mobile' })}
													>
														{item.title}
														<ChevronRight className="size-5 text-gray-muted group-hover:text-orange transition-colors" />
													</Link>
												))}
											</div>
											<div className="flex flex-col gap-4">
												{/* Mobile Utility Links (from TopBar) */}
												<Link
													href="/over-ons"
													className="text-blue-dark font-heading font-medium text-lg flex items-center justify-between group"
												>
													Over ons
													<ChevronRight className="size-5 text-gray-muted group-hover:text-orange transition-colors" />
												</Link>
												<Link
													href="/werken-bij"
													className="text-blue-dark font-heading font-medium text-lg flex items-center justify-between group"
												>
													Werken bij
													<ChevronRight className="size-5 text-gray-muted group-hover:text-orange transition-colors" />
												</Link>
												<Link
													href="/advies"
													className="text-blue-dark font-heading font-medium text-lg flex items-center justify-between group"
												>
													Advies & Inspiratie
													<ChevronRight className="size-5 text-gray-muted group-hover:text-orange transition-colors" />
												</Link>
											</div>
										</nav>
										<div className="pb-8 flex flex-col gap-4">
											<Button
												asChild
												className="w-full bg-orange hover:bg-orange-hover text-white font-bold text-lg py-6 shadow-md"
											>
												<Link href={ctaItem ? getHref(ctaItem) : '/contact'} onClick={() => trackEvent('navigation_click', { item: 'cta_offerte', location: 'mobile' })}>
													{ctaItem ? ctaItem.title : 'Offerte aanvragen'}
												</Link>
											</Button>
											<Button
												asChild
												className="w-full bg-blue-dark hover:bg-blue text-white font-bold text-lg py-6 shadow-md"
											>
												<a href="tel:0103075610" className="flex items-center justify-center gap-2" onClick={() => trackEvent('phone_click', { location: 'mobile_menu' })}>
													<Phone className="size-5 fill-current" />
													Bel direct
												</a>
											</Button>
										</div>
									</SheetContent>
								</Sheet>
							</div>
						)}
					</Container>
				</div>
			)}
		</header>
		</>
	);
});

NavigationBar.displayName = 'NavigationBar';
export default NavigationBar;
